import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { useEffect } from 'react';

import { cn } from '@/utils/classname';

interface NodeHandlesProps {
  isEditMode: boolean;
  /** Node id — required to re-measure handle bounds when orientation flips. */
  nodeId?: string;
  /** Where the incoming (target) handle sits. Defaults to Left (LR flow). */
  targetPosition?: Position;
  /** Where the outgoing (source) handle sits. Defaults to Right (LR flow). */
  sourcePosition?: Position;
}

/**
 * Reusable handle set for attack path nodes.
 *
 * Horizontal (LR) flow — the default — puts the target handle on the Left and
 * the source handle on the Right. Vertical (TB) flow puts target on Top and
 * source on Bottom; the main graph drives this per node via
 * sourcePosition/targetPosition (see AttackPathsGraph). Consumers that don't set
 * them (e.g. StageMiniGraph, which keeps its own LR layout) get LR handles.
 *
 * When the positions change, ReactFlow must re-measure the cached handle bounds,
 * so useUpdateNodeInternals is keyed on the resolved positions.
 */
export function NodeHandles({
  isEditMode,
  nodeId,
  targetPosition = Position.Left,
  sourcePosition = Position.Right,
}: NodeHandlesProps) {
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    if (nodeId) updateNodeInternals(nodeId);
  }, [nodeId, targetPosition, sourcePosition, updateNodeInternals]);

  const handleClass = cn(
    'bg-chariot-text-primary size-3 border-none',
    !isEditMode && 'invisible'
  );

  return (
    <>
      <Handle className={handleClass} type="target" position={targetPosition} />
      <Handle className={handleClass} type="source" position={sourcePosition} />
    </>
  );
}
